/**
 * Stack and Priority data for the MTG Rules Reference.
 * Defines stack mechanics, priority rules, LIFO resolution,
 * split second, special actions, and mana abilities.
 *
 * CommonJS module for compatibility with both backend (require) and
 * frontend (import via Webpack/Babel interop).
 *
 * @module rules/stackData
 */

const STACK_SECTION_IDS = {
  WHAT_USES_STACK: 'what-uses-the-stack',
  PRIORITY: 'priority-rules',
  LIFO: 'lifo-resolution',
  SPLIT_SECOND: 'split-second',
  SPECIAL_ACTIONS: 'special-actions',
  MANA_ABILITIES: 'mana-abilities',
};

const STACK_AND_PRIORITY = [
  {
    id: STACK_SECTION_IDS.WHAT_USES_STACK,
    title: 'What Goes on the Stack',
    content:
      'The following use the stack and can be responded to:\n\n• Spells (all card types when cast: creatures, instants, sorceries, artifacts, enchantments, planeswalkers, battles)\n• Activated abilities (abilities with a cost and effect separated by a colon, e.g., "{T}: Draw a card")\n• Triggered abilities (abilities that begin with "when," "whenever," or "at")\n\nEach spell or ability is placed on top of the stack when cast or triggered. Objects on the stack resolve one at a time from the top.',
    examples: [
      {
        scenario: 'A player casts Lightning Bolt targeting an opponent. Lightning Bolt goes on the stack.',
        resolution: 'The opponent can respond by casting a spell or activating an ability before it resolves.',
      },
      {
        scenario: 'A player casts a creature spell. An opponent responds with Counterspell, which goes on top of the stack.',
        resolution: 'The Counterspell resolves first (LIFO), countering the creature spell before it ever enters the battlefield.',
      },
    ],
  },
  {
    id: STACK_SECTION_IDS.PRIORITY,
    title: 'Priority Rules',
    content:
      'Priority determines who may take an action at any given moment. The rules for priority are:\n\n• The active player (the player whose turn it is) receives priority at the beginning of each step and phase (except untap and most cleanup steps).\n• After a player casts a spell, activates an ability, or a triggered ability is placed on the stack, that player receives priority again.\n• A player who has priority may cast an instant or a spell with flash, activate an ability, or take a special action.\n• A player may also choose to pass priority. When all players pass priority in succession without taking any action, the topmost object on the stack resolves.\n• After an object resolves, the active player receives priority again.\n• If the stack is empty and all players pass priority in succession, the current step or phase ends and the next one begins.',
    examples: [
      {
        scenario: 'Player A (active) casts Giant Growth on their creature, then passes priority. Player B casts Lightning Bolt targeting the same creature in response, then passes. Player A passes.',
        resolution: 'Lightning Bolt resolves first, dealing 3 damage. Then Giant Growth resolves, giving +3/+3. The creature survives if it had enough toughness after the Bolt.',
      },
      {
        scenario: 'Player A casts a sorcery during their main phase. Player B passes priority.',
        resolution: 'The sorcery resolves. Player A receives priority again and can cast another spell or pass to end the phase.',
      },
    ],
  },
  {
    id: STACK_SECTION_IDS.LIFO,
    title: 'LIFO Resolution',
    content:
      'The stack uses Last In, First Out (LIFO) order. The most recently added spell or ability resolves first. This is the fundamental principle that allows players to respond to each other\'s actions.\n\nWhen an object resolves, it is removed from the stack. If it is a permanent spell (creature, artifact, enchantment, planeswalker, battle), it enters the battlefield. If it is an instant or sorcery, its effect happens and it goes to the graveyard. If it is an activated or triggered ability, its effect happens and it ceases to exist.',
    examples: [
      {
        scenario: 'Player A casts Grizzly Bears (a 2/2 creature). Player B responds with Essence Scatter. Player A responds with Negate targeting Essence Scatter.',
        resolution: 'Resolution order: Negate resolves first, countering Essence Scatter. Then Grizzly Bears resolves and enters the battlefield.',
      },
      {
        scenario: 'Three Lightning Bolts are on the stack targeting the same player.',
        resolution: 'The last one cast resolves first, then the second, then the first. The player takes 3 damage three times (9 total).',
      },
    ],
  },
  {
    id: STACK_SECTION_IDS.SPLIT_SECOND,
    title: 'Split Second',
    content:
      'Split second is a keyword that says "As long as this spell is on the stack, players can\'t cast other spells or activate abilities that aren\'t mana abilities." This effectively prevents most responses to the spell.\n\nHowever, split second does NOT prevent:\n• Triggered abilities from triggering and being placed on the stack\n• Mana abilities from being activated\n• Special actions (such as turning a face-down creature face up)\n• Uncounterable effects from other sources already on the stack',
    examples: [
      {
        scenario: 'A player casts Krosan Grip (split second) to destroy an artifact.',
        resolution: 'The opponent cannot cast spells or activate abilities in response, but if a triggered ability would trigger (such as "whenever a player casts a spell"), it still triggers and is placed on the stack.',
      },
      {
        scenario: 'A player casts Sudden Spoiling (split second) targeting an opponent.',
        resolution: 'The opponent cannot respond with Giant Growth or activate abilities, but they can still activate mana abilities to float mana.',
      },
    ],
  },
  {
    id: STACK_SECTION_IDS.SPECIAL_ACTIONS,
    title: 'Special Actions',
    content:
      'Special actions do not use the stack and cannot be responded to. They happen immediately when a player takes them. The special actions are:\n\n• Playing a land (once per turn, during your main phase, while the stack is empty)\n• Turning a face-down creature face up (morph/manifest)\n• Suspending a card from your hand\n• Foretelling a card (exiling it face down from your hand)\n• Putting a companion from outside the game into your hand\n• Rolling the planar die (Planechase format)\n• Turning a face-down conspiracy card face up (Conspiracy draft)\n\nBecause these do not use the stack, opponents cannot respond to them. A land being played cannot be "countered."',
    examples: [
      {
        scenario: 'A player plays a land during their main phase.',
        resolution: 'This does not use the stack, so the opponent cannot respond with Stifle or any other spell.',
      },
      {
        scenario: 'A player pays the morph cost to turn a face-down Willbender face up.',
        resolution: 'This is a special action that does not use the stack. The opponent cannot respond to the creature being turned face up, but Willbender\'s triggered ability ("When Willbender is turned face up") does use the stack and can be responded to.',
      },
    ],
  },
  {
    id: STACK_SECTION_IDS.MANA_ABILITIES,
    title: 'Mana Abilities',
    content:
      'Mana abilities are a special category of abilities that produce mana. They do not use the stack and resolve immediately. An activated ability is a mana ability if it meets ALL of these criteria:\n\n• It could produce mana when it resolves\n• It is not a loyalty ability (planeswalker abilities are never mana abilities)\n• It does not target\n\nA triggered ability is a mana ability if it triggers from the activation or resolution of an activated mana ability and could produce mana.\n\nBecause mana abilities do not use the stack, they cannot be responded to. Tapping a land for mana happens instantly.',
    examples: [
      {
        scenario: 'A player taps a Forest for green mana.',
        resolution: 'This is a mana ability. It does not use the stack and cannot be countered or responded to.',
      },
      {
        scenario: 'A player activates Deathrite Shaman\'s ability: "{T}: Exile target land card from a graveyard. Add one mana of any color."',
        resolution: 'This is NOT a mana ability because it targets. It uses the stack and can be responded to.',
      },
    ],
  },
];

module.exports = {
  STACK_SECTION_IDS,
  STACK_AND_PRIORITY,
};
